// @flow
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import PdfFileRenderer from './PdfFileRenderer';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/ext-language_tools';
import Snackbar from '@mui/material/Snackbar';

const renderFileName = (filename) => {
  if (!filename) return '';
  const MAX_CHARS = 100;

  if (filename.length <= MAX_CHARS) return filename;
  const extension = filename.slice(filename.lastIndexOf('.'));
  const truncatedName = filename.slice(0, MAX_CHARS - extension.length - 3);

  return truncatedName + '...' + extension;
};
type Props = {
  pdfUrl: String,
  fileName: String,
  previewPage: Number,
  boundingBox: Object | null,
  imageDimension: Object | null,
  ocr: Boolean,
  searchText: String,
  metaDataPreview: String,
  metaDataDebug: String
};

const PdfFileViewer = (props: Props) => {
  const {
    pdfUrl,
    fileName,
    previewPage,
    boundingBox,
    imageDimension,
    ocr,
    searchText,
    metaDataPreview,
    metaDataDebug
  } = props;
  const [loading, setLoading] = useState(false);
  const [fileBin, setFileBin] = useState(null);
  const [showFileMetaDataDebug, setShowFileMetaDataDebug] = useState(false);
  const [pdfFullHeight, setPdfFullHeight] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [snackbackOpen, setSnackbackOpen] = useState(false);
  const [snackbackMessage, setSnackbackMessage] = useState('');

  const downloadPdfBin = async (pdfUrl) => {
    setLoading(true);
    setHasError(false);

    try {
      const response = await axios.get(pdfUrl, {
        responseType: 'arraybuffer'
      });
      const fileBlob = new Blob([response.data], { type: 'application/pdf' });
      setFileBin(fileBlob);
    } catch (error) {
      setHasError(true);
      setSnackbackMessage(
        error && error.message ? error.message : 'Unexpected Error occured'
      );
      setSnackbackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackBar = useCallback(() => {
    setSnackbackOpen(false);
  }, []);

  useEffect(() => {
    downloadPdfBin(pdfUrl);
  }, [pdfUrl]);

  if (loading) {
    return (
      <div className="flex justify-center w-full">
        <CircularProgress />
      </div>
    );
  }

  return (
    <Grid
      container
      direction="column"
      style={{
        height: '100%',
        flexWrap: 'nowrap',
        padding: '1rem',
        maxHeight: '100vh'
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbackOpen}
        onClose={handleCloseSnackBar}
        autoHideDuration={5000}
        message={snackbackMessage}
      />
      {!pdfFullHeight && (
        <Grid item>
          <Typography
            variant="h1"
            className="file-info-title !mb-2.5 !font-semibold !text-[1.7rem]"
          >
            {renderFileName(fileName)}
          </Typography>
        </Grid>
      )}
      {metaDataPreview && !pdfFullHeight && (
        <Grid item>
          <div className="relative flex flex-wrap [&_p]:text-base">
            {Object.keys(metaDataPreview).map((key, idx) => (
              <div
                className="file-info-row mb-[10px] flex items-center basis-1/2"
                key={idx}
              >
                <Typography variant="body1" className="file-info-key w-[200px]">
                  {key}:
                </Typography>
                <Typography
                  variant="body1"
                  className="font-semibold file-info-value"
                >
                  {metaDataPreview[key]}
                </Typography>
              </div>
            ))}
          </div>
        </Grid>
      )}
      <Grid item>
        {metaDataDebug && !pdfFullHeight && (
          <Button
            onClick={() => setShowFileMetaDataDebug((prevState) => !prevState)}
            variant="outlined"
          >
            {showFileMetaDataDebug ? 'Hide' : 'Show'} File Meta Data
          </Button>
        )}
      </Grid>
      {showFileMetaDataDebug && metaDataDebug && !pdfFullHeight && (
        <Grid item>
          <AceEditor
            width="100%"
            placeholder="Query Data"
            mode="json"
            theme="monokai"
            name="query-data"
            fontSize={14}
            showPrintMargin={true}
            showGutter={true}
            highlightActiveLine={true}
            value={JSON.stringify(metaDataDebug, null, 2)}
            setOptions={{
              readOnly: true,
              maxLines: Infinity
            }}
          />
        </Grid>
      )}
      {!hasError ? (
        <PdfFileRenderer
          fileBin={fileBin}
          fileName={fileName}
          previewPage={previewPage ? parseInt(previewPage) : null}
          boundingBox={boundingBox}
          imageDimension={imageDimension}
          ocr={ocr}
          searchText={searchText}
          pdfFullHeight={pdfFullHeight}
          setPdfFullHeight={setPdfFullHeight}
        />
      ) : (
        'No PDF available'
      )}
    </Grid>
  );
};

export default PdfFileViewer;
